import type {
  FocusScheduleBlock,
  FocusScheduleTimeEntry,
} from './focus-schedule'
import { focusBlocksOverlap } from './focus-schedule'

export interface FocusSyncResult {
  blocks: FocusScheduleBlock[]
  /** Blocks created from timed entries that had no planned counterpart. */
  added: number
  /** Planned blocks that adopted an entry, followed a changed one, or were released. */
  updated: number
  /** Synced blocks whose entry was deleted on the Log Time page. */
  removed: number
  /** Untimed entries with nothing planned to attach to; the panel shows them instead. */
  unplaced: number
}

export interface FocusSyncOptions {
  /** Work refs the schedule may reference; entries on anything else become custom blocks. */
  knownRefs?: Iterable<string>
  makeId?: () => string
}

function defaultId() {
  const api = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  return api?.randomUUID
    ? api.randomUUID()
    : `sync-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Make the planned day agree with logged time. Logged time is the truth:
 *
 * - a block already tied to an entry follows the entry's times and is done;
 * - an entry on planned work adopts the overlapping (else earliest unfinished)
 *   block for the same ref and marks it done, so nothing is duplicated;
 * - a timed entry with no planned counterpart becomes a done block;
 * - an untimed entry with no counterpart cannot be placed and is counted;
 * - a synced block whose entry vanished is removed if the sync created it,
 *   otherwise released (no longer done, no entry).
 *
 * Running it twice changes nothing the second time.
 */
export function syncScheduleWithTime(
  blocks: FocusScheduleBlock[],
  entries: FocusScheduleTimeEntry[],
  options: FocusSyncOptions = {},
): FocusSyncResult {
  const known = options.knownRefs ? new Set(options.knownRefs) : null
  const makeId = options.makeId ?? defaultId
  const entryIds = new Set(entries.map(entry => entry.id))
  let added = 0
  let updated = 0
  let removed = 0
  let unplaced = 0

  let next: FocusScheduleBlock[] = []
  for (const original of blocks) {
    const block = { ...original }
    if (block.timeEntryId && !entryIds.has(block.timeEntryId)) {
      if (block.fromTime) {
        removed += 1
        continue
      }
      delete block.timeEntryId
      block.done = false
      updated += 1
    }
    next.push(block)
  }

  for (const entry of entries) {
    const timed =
      entry.startMinute !== null &&
      entry.endMinute !== null &&
      entry.endMinute > entry.startMinute
    const start = timed ? Math.max(0, entry.startMinute!) : null
    const end = timed ? Math.min(1440, entry.endMinute!) : null
    let block = next.find(item => item.timeEntryId === entry.id)
    if (!block && entry.ref) {
      const candidates = next.filter(
        item => item.workRef === entry.ref && !item.timeEntryId,
      )
      block =
        (timed
          ? candidates.find(item =>
              focusBlocksOverlap(item, {
                startMinute: start!,
                endMinute: end!,
              }),
            )
          : undefined) ??
        candidates.find(item => !item.done) ??
        candidates[0]
      if (block) {
        block.timeEntryId = entry.id
        block.done = true
        if (timed) {
          block.startMinute = start!
          block.endMinute = end!
        }
        updated += 1
        continue
      }
    }
    if (block) {
      const moved =
        timed && (block.startMinute !== start || block.endMinute !== end)
      if (!block.done || moved) {
        block.done = true
        if (timed) {
          block.startMinute = start!
          block.endMinute = end!
        }
        updated += 1
      }
      continue
    }
    if (!timed) {
      unplaced += 1
      continue
    }
    next.push({
      id: makeId(),
      title: entry.title,
      kind: 'work',
      startMinute: start!,
      endMinute: end!,
      done: true,
      timeEntryId: entry.id,
      fromTime: true,
      ...(entry.ref && (!known || known.has(entry.ref))
        ? { workRef: entry.ref }
        : {}),
    })
    added += 1
  }

  next = next.sort((a, b) => a.startMinute - b.startMinute)
  return { blocks: next, added, updated, removed, unplaced }
}
